import React, { useState, useEffect, useRef } from "react";
import { CreateContact } from "./CreateContact";
import { EditDetail } from "./EditDetail";
import "../styles/Contacts.css";
import { Baby, ChevronDown, Languages, Smile, Meh, Frown, SmilePlus } from "lucide-react";
import Alert from "./Alert";
import { formatPhoneNumber } from "./FieldComponents";
import { SearchBar } from "./Nav";
import { useOptimizedResize, throttle } from '../utils/performance';
import { getContactInitialsClassName } from '../utils/colorUtils';
import Modal from "./Modal";
import { useNavVariant } from '../App';
import { useParams } from 'react-router-dom';

export const Contacts = () => {
    const { contactId: routeContactId } = useParams();
    const [createContact, setCreateContact] = useState(false);
    const [editContact, setEditContact] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const navVariant = useNavVariant();
    const [renderMobile, setRenderMobile] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [contactTypes, setContactTypes] = useState([]);
    const [sortBy, setSortBy] = useState("full_name");
    const [searchQuery, setSearchQuery] = useState('');
    const [tagSearch, setTagSearch] = useState('');
    const [addressSearch, setAddressSearch] = useState('');
    const [error, setError] = useState(null);
    const [centerLetter, setCenterLetter] = useState('*');
    const [scrolledDown, setScrolledDown] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 100,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [perPageOptions] = useState([25, 50, 100, 500, 1000]);
    const [showNatureModal, setShowNatureModal] = useState(false);
    const [selectedNature, setSelectedNature] = useState(null);
    const [natureDescription, setNatureDescription] = useState('');
    const [selectedContactId, setSelectedContactId] = useState(null);
    const alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
    const [pageCount, setPageCount] = useState(3);
    const rowRefs = useRef({});
    const autoOpenedContactIdRef = useRef(null);
    const url = new URLSearchParams(window.location.search);
    const params = new URL(window.location.href);
    const deepLinkContactId = routeContactId || url.get('contact_id') || url.get('id');

    const getContactDisplayName = (contact) => {
        if (!contact) return '';

        return (
            contact.full_name?.trim()
            || contact.company_name?.trim()
            || [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim()
            || `Contact ${contact.id}`
        );
    };
    
    useEffect(() => {
        document.title = "Contacts | CaseDB";
        if (url.get("new") === "true") {
            setCreateContact(true);
        }
    }, []);

    useEffect(() => {
        fetchContacts();
        if (createContact === false && url.get("new") === "true") {
            params.searchParams.delete("new");
            window.history.replaceState(null, "", params.toString());
        }
        console.log(createContact, editContact);
    }, [createContact, editContact]);

    useOptimizedResize(() => {
        const width = window.innerWidth;
        setScreenWidth(width);
        setRenderMobile(width < 768);
        
        if (width < 768) {
            setPageCount(2);
        } else {
            setPageCount(3);
        }
    });

    const fetchContacts = async (page = 1, perPage = pagination.per_page) => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const showOpportunities = urlParams.get('opportunities') === 'true';
            
            const params = new URLSearchParams({
                page: page.toString(),
                per_page: perPage.toString(),
                sort_by: 'full_name',
                search: searchQuery,
                tag_search: tagSearch,
                address_search: addressSearch,
                time: new Date().getTime().toString()
            });
            
            // Add opportunities parameter if present in URL
            if (showOpportunities) {
                params.append('opportunities', 'true');
            }
            
            const response = await fetch(`https://api.casedb.co/contacts.php?${params}`, { credentials: 'include' });
            const data = await response.json();
            setContacts(data.contacts);
            setContactTypes(data.contact_types);
            if (data.pagination) {
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error("Error fetching contacts:", error);
        }
    };

    const fetchContactById = async (contactId) => {
        try {
            const response = await fetch(`https://api.casedb.co/contacts.php?id=${encodeURIComponent(contactId)}&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();

            if (!response.ok || data.success === false || !data.contacts?.id) {
                throw new Error(data.message || data.error || 'Contact not found');
            }

            const contact = data.contacts;
            const contactName = getContactDisplayName(contact);

            setContacts([contact]);
            setContactTypes(data.contact_types || []);
            setPagination(prev => ({
                ...prev,
                current_page: 1,
                total: 1,
                total_pages: 1,
                has_next: false,
                has_prev: false
            }));
            setSearchQuery(contactName);
            setSelectedContact(contact);
            setEditContact(true);
        } catch (error) {
            console.error('Error opening contact:', error);
            setError({ type: 'error', message: error.message || 'Unable to open contact.' });
        }
    };

    useEffect(() => {
        const normalizedContactId = String(deepLinkContactId || '').trim();
        if (!normalizedContactId || autoOpenedContactIdRef.current === normalizedContactId) return;

        autoOpenedContactIdRef.current = normalizedContactId;
        fetchContactById(normalizedContactId);
    }, [deepLinkContactId]);

    // Add effect to refetch when search parameters change (removed sortBy dependency)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchContacts(1, pagination.per_page); // Reset to page 1 when search changes
        }, 300); // Debounce search input

        return () => clearTimeout(timeoutId);
    }, [searchQuery, tagSearch, addressSearch]);

    const handleClick = (contact) => {
        setSelectedContact(contact);
        setEditContact(true);
    };

    const handleOpenNatureModal = (e, contactId, nature, description) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedContactId(contactId);
        setSelectedNature(nature || null);
        setNatureDescription(description || '');
        setShowNatureModal(true);
    };

    const handleSaveNature = async () => {
        try {
            const response = await fetch('https://api.casedb.co/contacts.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    contact_id: selectedContactId,
                    nature: selectedNature,
                    nature_description: natureDescription
                })
            });

            const data = await response.json();
            if (data.success) {
                setShowNatureModal(false);
                fetchContacts(pagination.current_page, pagination.per_page);
            } else {
                console.error('Failed to update nature');
            }
        } catch (error) {
            console.error('Error updating nature:', error);
        }
    };

    const handleLetterClick = (letter) => {
        setCenterLetter(letter);

        const match = contacts.find(contact => {
            // Use consistent sorting key logic
            const sortKey = contact.full_name || contact.first_name || contact.company_name || "";
            return sortKey.toString().toUpperCase().startsWith(letter);
        });

        if (match && rowRefs.current[match.id]) {
            const row = rowRefs.current[match.id];
            const offsetTop = row.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: offsetTop - 100,
                behavior: 'smooth'
            });
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.total_pages) {
            fetchContacts(newPage, pagination.per_page);
        }
    };

    const handlePerPageChange = (newPerPage) => {
        fetchContacts(1, newPerPage);
    };

    useEffect(() => {
        const handleScroll = throttle(() => {
            const scrollY = window.scrollY;
            setScrolledDown(scrollY > 10);

            const viewportMiddle = window.innerHeight / 2;
            let closestContact = null;
            let closestDistance = Infinity;

            if(Array.isArray(contacts)) {
                for (const contact of contacts) {
                    const row = rowRefs.current[contact.id];
                    if (row) {
                        const rect = row.getBoundingClientRect();
                        const rowMiddle = rect.top + rect.height / 2;
                        const distance = Math.abs(viewportMiddle - rowMiddle);

                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestContact = contact;
                        }
                    }
                }
            }

            if (closestContact) {
                const letter = (closestContact.full_name || closestContact.first_name || closestContact.company_name || '')
                    .toString()
                    .charAt(0)
                    .toUpperCase();
                setCenterLetter(letter || '*');
            }
        }, 16);

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, [contacts]);

    const generatePageNumbers = () => {
        const total = pagination.total_pages;
        const current = pagination.current_page;
        const range = pageCount;

        const pages = new Set();

        pages.add(1);
        if (total > 1) pages.add(2);

        pages.add(total);
        if (total > 1) pages.add(total - 1);

        for (let i = current - 1; i <= current + 1; i++) {
            if (i > 1 && i < total) pages.add(i);
        }

        const sorted = Array.from(pages).sort((a, b) => a - b);
        while (sorted.length > range) {
            const mid = Math.floor(sorted.length / 2);
            if (Math.abs(sorted[mid] - current) > 1) {
                pages.delete(sorted[mid]);
            } else {
                break;
            }
            sorted.length = 0;
            pages.forEach(p => sorted.push(p));
            sorted.sort((a, b) => a - b);
        }

        return sorted;
    };

    const visiblePages = generatePageNumbers();
    let lastPage = 0;
    const showPhoneColumn = screenWidth >= 600;
    const showEmailColumn = screenWidth >= 768;
    const showAddressColumn = screenWidth >= 1050;
    const visibleContactColumnCount = 1 + (showAddressColumn ? 1 : 0) + (showEmailColumn ? 1 : 0) + (showPhoneColumn ? 1 : 0);
    const contactNameColumnStyle = { width: `${100 / visibleContactColumnCount}%` };
    const addressColumnStyle = { width: showAddressColumn && visibleContactColumnCount === 4 ? '35%' : `${100 / visibleContactColumnCount}%` };
    const contactSecondaryColumnStyle = { width: `${100 / visibleContactColumnCount}%` };

    return (
        <>
            <div className={`page-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
                <div id="page-header">
                    <h2>{new URLSearchParams(window.location.search).get('opportunities') === 'true' ? 'Opportunities' : 'Contacts'}</h2>
                    <button className="action" onClick={() => setCreateContact(true)}>Create {new URLSearchParams(window.location.search).get('opportunities') === 'true' ? 'Opportunity' : 'Contact'}</button>
                    <div className='active-cases subtext'>
                        <p>Contacts: {pagination?.total || contacts?.length}</p>
                    </div>
                </div>
                <div className='case-filters'>
                    <SearchBar 
                        expanded={true} 
                        setExpanded={() => {}} 
                        placeholder={`Search contacts`} 
                        setSearchQuery={setSearchQuery}
                    />
                    {screenWidth >= 768 && (
                        <>
                            <div className='form-group nm small'>
                                <input type='text' placeholder='Search Tags' value={tagSearch} onChange={(e) => setTagSearch(e.target.value)} />
                            </div>
                            <div className='form-group nm small'>
                                <input type='text' placeholder='Search Address (line, city, state, zip)' value={addressSearch} onChange={(e) => setAddressSearch(e.target.value)} />
                            </div>
                            <div className="filter-group">
                                <div className="select-wrapper">
                                    <select 
                                        className='filter-select' 
                                        value={pagination.per_page} 
                                        onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                                    >
                                        {perPageOptions.map(option => (
                                            <option key={option} value={option}>{option} per page</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                            {(() => {
                                const urlParams = new URLSearchParams(window.location.search);
                                return urlParams.get('opportunities') === 'true' ? (
                                    <div className="filter-tag" style={{ marginLeft: '10px' }}>
                                        Opportunities Only
                                        <button 
                                            className="filter-tag-remove"
                                            onClick={() => {
                                                const newUrl = new URL(window.location);
                                                newUrl.searchParams.delete('opportunities');
                                                window.history.pushState({}, '', newUrl);
                                                window.location.reload();
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : null;
                            })()}
                        </>
                    )}
                </div>
                
                {/* Pagination Controls */}
                <div className='pagination-controls' style={{ justifyContent: 'center', marginTop: "10px", padding: "10px 0" }}>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_prev}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                    >
                        Previous
                    </button>
                    <span className='pagination-info subtext'>
                        Page {pagination.current_page} of {pagination.total_pages} ({pagination.total} total contacts)
                    </span>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_next}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                    >
                        Next
                    </button>
                </div>

                <div className='contacts-table-container'>
                    {/*!renderMobile ? (
                            <div className={`every-letter ${scrolledDown ? 'scrolled' : 'not-scrolled'}`}>
                                {alphabet.map(letter => (
                                    <span
                                        key={letter}
                                        className={`letter ${centerLetter === letter ? 'active' : ''}`}
                                        onClick={() => handleLetterClick(letter)}
                                    >
                                        {letter}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <select className='default-select every-letter' value={centerLetter} onChange={(e) => setCenterLetter(e.target.value)}>
                                {alphabet.map(letter => (
                                    <option key={letter} value={letter}>{letter}</option>
                                ))}
                            </select>
                        )
                    */}
                    <table className="contacts-table default">
                        <thead>
                            <tr className="default">
                                <th style={contactNameColumnStyle}>Name</th>
                                {showAddressColumn && <th style={addressColumnStyle}>Address</th>}
                                {showEmailColumn && <th style={contactSecondaryColumnStyle}>Email</th>}
                                {showPhoneColumn && <th style={contactSecondaryColumnStyle}>Phone</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(contacts) && contacts?.map((contact) => (
                                <tr key={contact.id} ref={el => rowRefs.current[contact.id] = el} className="contact default" onClick={() => handleClick(contact)}>
                                    <td style={contactNameColumnStyle}>
                                        {contact.profile_picture ? (
                                            <img className={getContactInitialsClassName(contact.id)} src={`https://api.casedb.co/${contact.profile_picture}`} alt="Profile" />
                                        ) : (
                                            <div className={getContactInitialsClassName(contact.id)}>
                                                <span>
                                                    {contact?.first_name ? contact.first_name[0] : contact.company_name?.[0] || "F"}
                                                    {contact?.last_name ? contact.last_name[0] : contact.company_name ? "" : contact.first_name === null ? "L" : ""}
                                                </span>
                                            </div>
                                        )}
                                        <div className="contact-name-row small-text">
                                            <span>
                                                {
                                                    (
                                                        contact.full_name?.trim()
                                                        ? `${contact.prefix ? contact.prefix + ' ' : ''}${contact.full_name}${contact.suffix ? ' ' + contact.suffix : ''}`
                                                        : contact.company_name?.trim()
                                                        || 'Unnamed Contact'
                                                    ).slice(0, 50)
                                                }
                                            </span>
                                            <div className='contact-nature' onClick={(e) => handleOpenNatureModal(e, contact?.contact_id, contact?.nature, contact?.nature_description)} style={{ cursor: 'pointer' }}>
                                                {Number(contact?.nature) === 1 ? <Smile size={18} className="contact-nature-icon well-natured" /> : Number(contact?.nature) === 2 ? <Meh size={18} className="contact-nature-icon neutral-natured" /> : Number(contact?.nature) === 3 ? <Frown size={18} className="contact-nature-icon ill-natured" /> : <SmilePlus size={18} className="contact-nature-icon subtext" />}
                                            </div>
                                            {contact?.language && contact.language.toLowerCase() !== 'english' && (
                                                <Languages 
                                                    size={16} 
                                                    className="language-indicator" 
                                                    title={`Needs an interpreter for ${contact.language}`}
                                                />
                                            )}
                                        </div>
                                        {Number(contact.is_company) !== 1 && new Date(contact?.date_of_birth).getTime() >= new Date(new Date().setFullYear(new Date().getFullYear() - 18)) ? <Baby size={18} /> : ""}
                                        {!renderMobile && contact.contact_type && <span className="tag" title={contact.contact_type}>#{String(contact.contact_type).slice(0, 12)}{contact.contact_type.length > 12 ? '...' : ''}</span>}
                                    </td>
                                    {showAddressColumn && (
                                        <td style={addressColumnStyle} className='contacts-address small-text'>
                                            {contact.addresses[0]?.line1 ? `${contact.addresses[0]?.line1}, ` : ""}
                                            {contact.addresses[0]?.line2 ? `${contact.addresses[0]?.line2}, ` : ""}
                                            {contact.addresses[0]?.city ? `${contact.addresses[0]?.city}, ` : ""}
                                            {contact.addresses[0]?.state ? `${contact.addresses[0]?.state}` : ""}
                                            {contact?.addresses[0]?.postal_code || contact?.addresses[0]?.zip ? ` ${contact.addresses[0]?.postal_code || contact?.addresses[0]?.zip}` : ''}
                                        </td>
                                    )}
                                    {showEmailColumn && <td style={contactSecondaryColumnStyle} className='small-text'>{contact.emails.length > 0 ? contact.emails[0].email : ""}</td>}
                                    {showPhoneColumn && <td style={contactSecondaryColumnStyle} className='small-text'>{contact.phones.length > 0 ? formatPhoneNumber(contact.phones[0].number) : ""}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className='pagination-controls' style={{ justifyContent: 'center', marginTop: "10px", padding: "10px 0" }}>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_prev}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                    >
                        Previous
                    </button>
                    <div className="pagination-pages">
                        {visiblePages.map(page => {
                            const showEllipsis = page - lastPage > 1;
                            lastPage = page;

                            return (
                                <React.Fragment key={page}>
                                    {showEllipsis && <button className="action alt" disabled>...</button>}
                                    <button
                                        className={`action ${pagination.current_page === page ? '' : 'alt'}`}
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </button>
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_next}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>

            {createContact && (
                <CreateContact setCreateContact={setCreateContact} fetchContacts={fetchContacts} contactTypes={contactTypes} opportunity={true} />
            )}
            
            {editContact && selectedContact && (
                <EditDetail 
                    setEditContact={setEditContact} 
                    contactData={selectedContact} 
                    fetchContacts={fetchContacts} 
                    contactTypes={contactTypes} 
                    setError={setError}
                    setCreateContact={setCreateContact}
                />
            )}

            {error && <Alert message={error.message} type={error.type} onClose={() => setError(null)} />}

            {showNatureModal && (
                <Modal
                    title="Contact Nature"
                    onClose={() => setShowNatureModal(false)}
                    footer={
                        <div className='modal-footer-actions'>
                            <button
                                onClick={() => setShowNatureModal(false)}
                                className="action alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveNature}
                                className="action"
                                disabled={selectedNature === null}
                            >
                                Save
                            </button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper">
                        <div className='smiles-container'>
                            <div
                                onClick={() => setSelectedNature(1)}
                                className={`smile-container ${selectedNature === 1 ? 'selected' : ''}`}
                            >
                                <Smile size={30} className="contact-nature-icon well-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Well Natured</div>
                            </div>
                            <div
                                onClick={() => setSelectedNature(2)}
                                className={`smile-container ${selectedNature === 2 ? 'selected' : ''}`}
                            >
                                <Meh size={30} className="contact-nature-icon neutral-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Neutral</div>
                            </div>
                            <div
                                onClick={() => setSelectedNature(3)}
                                className={`smile-container ${selectedNature === 3 ? 'selected' : ''}`}
                            >
                                <Frown size={30} className="contact-nature-icon ill-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Ill Natured</div>
                            </div>
                        </div>

                        <div className='form-group full' style={{ maxWidth: '100%', width: '100%', marginBottom: '0' }}>
                            <label className="subtext">
                                Nature Description
                            </label>
                            <textarea
                                value={natureDescription}
                                onChange={(e) => setNatureDescription(e.target.value)}
                                placeholder="Add notes about this contact's nature..."
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};